/**
 * Power Plants Layer
 * GeoJSON-based power plant locations grouped by fuel type
 */
import mapboxgl from 'mapbox-gl';
import biomass_icon from '@/assets/icons/power-biomass.png';
import coal_icon from '@/assets/icons/power-coal.png';
import cogeneration_icon from '@/assets/icons/power-cogeneration.png';
import gas_icon from '@/assets/icons/power-gas.png';
import geothermal_icon from '@/assets/icons/power-geothermal.png';
import hydro_icon from '@/assets/icons/power-hydro.png';
import nuclear_icon from '@/assets/icons/power-nuclear.png';
import oil_icon from '@/assets/icons/power-oil.png';
import other_icon from '@/assets/icons/power-other.png';
import petcoke_icon from '@/assets/icons/power-petcoke.png';
import solar_icon from '@/assets/icons/power-solar.png';
import storage_icon from '@/assets/icons/power-storage.png';
import waste_icon from '@/assets/icons/power-waste.png';
import wave_tidal_icon from '@/assets/icons/power-wave-and-tidal.png';
import wind_icon from '@/assets/icons/power-wind.png';
import type { MapPopupProps } from '@/components/molecules/MapPopup';
import { setMapPopupContent } from '@/utils/map/mapPopup';
import { waitForStyleLoaded } from '../geoJsonLayerHelper';

const FUEL_TYPE_ICONS: Record<string, string> = {
  Hydro: hydro_icon,
  Solar: solar_icon,
  Gas: gas_icon,
  Other: other_icon,
  Oil: oil_icon,
  Wind: wind_icon,
  Nuclear: nuclear_icon,
  Coal: coal_icon,
  Waste: waste_icon,
  Biomass: biomass_icon,
  'Wave and Tidal': wave_tidal_icon,
  Petcoke: petcoke_icon,
  Geothermal: geothermal_icon,
  Storage: storage_icon,
  Cogeneration: cogeneration_icon,
};

interface PowerPlantFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: {
    id: number;
    name: string;
    primary_fuel: string;
    country_long: string;
    source: string;
    estimated_generation_gwh_2017: string;
    estimated_generation_note_2017: string;
  };
}

interface PowerPlantsGeoJSON {
  type: 'FeatureCollection';
  features: PowerPlantFeature[];
}

/**
 * Load image with timeout to prevent hanging
 */
function loadImageWithTimeout(map: mapboxgl.Map, iconPath: string, iconId: string, timeout = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      console.warn(`Timeout loading icon: ${iconId}`);
      resolve(false);
    }, timeout);

    map.loadImage(iconPath, (error: Error | null, image: ImageData | HTMLImageElement | null) => {
      clearTimeout(timer);
      if (error) {
        console.error(`Failed to load icon ${iconId}:`, error);
        resolve(false);
        return;
      }
      if (!map.hasImage(iconId)) {
        map.addImage(iconId, image);
      }
      resolve(true);
    });
  });
}

/**
 * Add Power Plants layer from pre-loaded GeoJSON
 */
export async function addPowerPlants(map: mapboxgl.Map | null): Promise<void> {
  if (!map) return;

  // Wait for style to be loaded
  const styleReady = await waitForStyleLoaded(map);
  if (!styleReady) {
    console.warn('PowerPlants: style not ready, skipping');
    return;
  }

  try {
    // Load GeoJSON data
    const response = await fetch('/data/powerplants.geojson');
    if (!response.ok) {
      throw new Error(`Failed to fetch powerplants.geojson: ${response.status}`);
    }
    const geojson: PowerPlantsGeoJSON = await response.json();

    // Group features by fuel type
    const byFuelType = new Map<string, PowerPlantFeature[]>();
    geojson.features.forEach((feature) => {
      const fuelType = feature.properties.primary_fuel;
      let features = byFuelType.get(fuelType);
      if (!features) {
        features = [];
        byFuelType.set(fuelType, features);
      }
      features.push(feature);
    });

    let _addedCount = 0;
    const _fuelTypes = Array.from(byFuelType.keys());

    // Create layer for each fuel type
    for (const [fuelType, features] of byFuelType) {
      const layerId = `Power Plants - ${fuelType} Layer`;
      const sourceId = `Power Plants - ${fuelType}`;
      const iconId = `power-${fuelType.toLowerCase().replace(/\s+/g, '-')}-icon`;
      const iconPath = FUEL_TYPE_ICONS[fuelType] || other_icon;

      const geojsonData: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: features as unknown as GeoJSON.Feature[],
      };

      // Re-check style is loaded before each layer (style can become unloaded during iteration)
      if (!map.isStyleLoaded()) {
        const ready = await waitForStyleLoaded(map);
        if (!ready) {
          console.warn(`PowerPlants [${fuelType}]: style timeout, skipping`);
          continue;
        }
      }

      try {
        // Remove existing layer/source if present
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }

        // Add source
        map.addSource(sourceId, {
          type: 'geojson',
          data: geojsonData,
        });

        // Try to load icon first
        let useIcon = false;
        if (!map.hasImage(iconId)) {
          try {
            const loaded = await loadImageWithTimeout(map, iconPath, iconId);
            useIcon = loaded;
          } catch {
            useIcon = false;
          }
        } else {
          useIcon = true;
        }

        // Add layer with icon or circle fallback
        if (useIcon) {
          map.addLayer({
            id: layerId,
            type: 'symbol',
            source: sourceId,
            layout: {
              'icon-image': iconId,
              'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.25, 11, 0.6],
              'icon-allow-overlap': false,
              'icon-ignore-placement': false,
              visibility: 'visible',
            },
          });
        } else {
          map.addLayer({
            id: layerId,
            type: 'circle',
            source: sourceId,
            paint: {
              'circle-radius': 4,
              'circle-color': '#facc15',
              'circle-stroke-width': 0.5,
              'circle-stroke-color': '#ffffff',
            },
            layout: {
              visibility: 'visible',
            },
          });
        }

        _addedCount++;
      } catch (err) {
        console.error(`PowerPlants [${fuelType}]: error adding layer`, err);
      }

      if (map.getLayer(layerId)) {
        const popup = new mapboxgl.Popup({ closeButton: false, offset: 16, maxWidth: '360px' });

        map.on('mouseenter', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
          map.getCanvas().style.cursor = 'pointer';
          const props = e.features?.[0]?.properties;
          if (!props) return;

          const popupProps: MapPopupProps = {
            badge: { label: 'Power Plant', variant: 'info' },
            title: props.name || 'Unknown facility',
            subtitle: props.country_long || undefined,
            sections: [
              {
                id: 'power-plant-details',
                fields: [
                  { label: 'Primary Fuel', value: props.primary_fuel || 'Unknown' },
                  { label: 'Country', value: props.country_long || 'Unknown' },
                ],
              },
            ],
          };

          setMapPopupContent(popup, e.lngLat, popupProps);
          popup.addTo(map);
        });

        map.on('mouseleave', layerId, () => {
          map.getCanvas().style.cursor = '';
          popup.remove();
        });
      }
    }
  } catch (error) {
    console.error('PowerPlants: error loading', error);
    throw error;
  }
}
